import { Component, Injector, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { AlertService } from '@lsq-frontend/angular-shared/features/alert-service';
import { CLAIMS } from '../../../constants/injector.constants';
import { IClaims } from '../../../models/common';
import { IterablePipe } from '../../../pipes/iterable.pipe';
import { ClaimService } from '../../../services/claim/claim.service';
import { ColumnType } from '../../../table/constants/column-type';
import { forLifeOf, LifecycleAware } from '../../../utilities/rxjs.helpers';
import { Utilities } from '../../../utilities/utilities.helper';

@LifecycleAware()
@Component({
    selector: 'claims',
    template: `
        <lsq-banner [titleText]="'Local Claims'"></lsq-banner>
        <div class="container-fluid page-container">
            <div class="row">
                <div class="col-12">
                    <form *ngIf="formGroup" [formGroup]="formGroup" (ngSubmit)="onSubmit()">
                        <div class="card">
                            <div class="card-header">Active Claims</div>
                            <div class="card-body">
                                <div class="row">
                                    <div
                                        class="col-3"
                                        *ngFor="let claim of claimsList | sort: 'label'"
                                    >
                                        <lsq-switch
                                            containerClass="flex-left-to-right"
                                            labelOrientation="right"
                                            labelClass="text-bold"
                                            [label]="formatLabelText(claim.label)"
                                            [name]="claim.key"
                                            [formControlName]="claim.key"
                                        >
                                        </lsq-switch>
                                    </div>
                                </div>
                            </div>
                            <div class="card-footer">
                                <div class="row">
                                    <div class="col">
                                        <button
                                            id="delete-local-button"
                                            type="button"
                                            class="btn btn-default btn-md btn-outline"
                                            [disabled]="submitting"
                                            (click)="handleDeleteLocalClick()"
                                        >
                                            Delete Local
                                        </button>
                                    </div>
                                    <div class="col text-right">
                                        <button
                                            id="save-changes-button"
                                            class="btn btn-success btn-md"
                                            [disabled]="submitting"
                                        >
                                            Save Changes
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    `
})
export class LsqClaimsComponent implements OnDestroy {
    private claimsEnum: { [key: string]: string };
    public submitting = false;
    public claims: IClaims;
    public claimsList: { key: string; label: string; value: number }[] = [];
    public formGroup: FormGroup;
    public columns = [
        {
            type: ColumnType.RowCounter,
            width: '4.5rem'
        },
        {
            name: 'Claim',
            field: 'claim'
        }
    ];

    constructor(
        private formBuilder: FormBuilder,
        private injector: Injector,
        private iterablePipe: IterablePipe,
        public alertService: AlertService,
        public claimService: ClaimService
    ) {
        this.claimsEnum = this.injector.get(CLAIMS);

        if (this.claimsEnum != null) {
            this.claimsList = this.iterablePipe.transform(this.claimsEnum);
        }

        this.claimService.claimStream.pipe(forLifeOf(this)).subscribe((x) => {
            this.processClaims(x);
        });
    }

    public ngOnDestroy(): void {}

    public createFormGroup(): FormGroup {
        const formGroup = this.formBuilder.group({});
        this.claimsList.forEach((x) =>
            formGroup.addControl(x.key, this.formBuilder.control(!!this.claims[`${x.value}`]))
        );
        return formGroup;
    }

    public mapFeatureFlagRequest(formClaims: object): void {
        this.claimsList.forEach((x) => {
            Object.keys(formClaims).forEach((formClaimKey) => {
                if (formClaimKey === x.key) {
                    this.claims[`${x.value}`] = !!formClaims[formClaimKey];
                }
            });
        });
    }

    public formatLabelText(label: string): string {
        return Utilities.kebabCaseToSentenceCase(label);
    }

    public onSubmit(): void {
        if (this.submitting) {
            return;
        }

        this.submitting = true;
        this.claimService.updateLocalClaims(this.claims);
        this.alertService.success('Local claims updated.');
        this.submitting = false;
    }

    public async handleDeleteLocalClick(): Promise<void> {
        await this.claimService.removeLocalClaims();
        this.alertService.success('Local claims removed.');
    }

    private processClaims(claims: IClaims) {
        this.claims = claims;
        this.formGroup = this.createFormGroup();
        this.formGroup.valueChanges.subscribe((c) => {
            this.mapFeatureFlagRequest(c);
        });
    }
}
